// src/utils/fetchCityImage.js

import cities from "../cities.json";

const imageCache = new Map();

export async function fetchCityImage(city, country) {
  const cacheKey = `${city}|${country}`;
  if (imageCache.has(cacheKey)) return imageCache.get(cacheKey);
  // 1. Robust Wikipedia Search (Finds page title first, then image)
  const fetchFromWikipedia = async () => {
    try {
      // Step A: Ask Wikipedia "Which page matches this city & country?"
      // We search for "City Country" (e.g. "Paris France", "Ottawa Canada")
      const searchQuery = `${city} ${country}`;
      const searchUrl = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(
        searchQuery
      )}&format=json&origin=*`;

      const searchRes = await fetch(searchUrl);
      const searchData = await searchRes.json();

      // If no results found, give up on Wiki
      if (!searchData?.query?.search?.length) return null;

      // Get the top result's title (e.g., returns "New York City" for "New York United States")
      const bestPageTitle = searchData.query.search[0].title;

      // Step B: Fetch the image for that specific page title
      const imageUrl = `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(
        bestPageTitle
      )}&prop=pageimages&format=json&pithumbsize=1000&origin=*`;

      const imageRes = await fetch(imageUrl);
      const imageData = await imageRes.json();

      const pages = imageData?.query?.pages;
      if (!pages) return null;

      // Wikipedia returns pages keyed by ID, so grab the first one
      const pageId = Object.keys(pages)[0];
      const source = pages[pageId]?.thumbnail?.source;

      if (source) {
        return source;
      }
    } catch (err) {
      console.warn("🌍 Wikipedia lookup failed for", city, err);
    }
    return null;
  };

  // 2. Unsplash Fallback (Better Keywords)
  const fetchFromUnsplash = async () => {
    const key = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
    if (!key) {
      // Quietly fail if no key is present
      return null;
    }

    // Add 'travel' and 'landmark' to prompt to avoid getting maps or flags
    const query = `${city} ${country} landmark travel`;
    const url = `https://api.unsplash.com/search/photos?client_id=${key}&query=${encodeURIComponent(
      query
    )}&orientation=landscape&per_page=1`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      // Return the 'regular' size (better quality than thumb/small)
      return data.results?.[0]?.urls?.regular || null;
    } catch (err) {
      console.warn("🖼️ Unsplash lookup failed for", city, err);
      return null;
    }
  };

  // --- Execution Strategy ---

  // 1. Try Wikipedia first (Unlimited, Free, Accurate)
  const wikiImage = await fetchFromWikipedia();
  if (wikiImage) {
    imageCache.set(cacheKey, wikiImage);
    return wikiImage;
  }

  // 2. Fallback to Unsplash
  const unsplashImage = await fetchFromUnsplash();
  if (unsplashImage) {
    imageCache.set(cacheKey, unsplashImage);
    return unsplashImage;
  }

  // 3. Return null (Consumer component will handle fallback)
  imageCache.set(cacheKey, null);
  return null;
}
